import React, { useEffect, useRef, useState } from 'react';
import { useSecurePlaybackStream } from '../../hooks/useSecurePlaybackStream';
import SecureVideo from './SecureVideo';
import VideoWatermark from './VideoWatermark';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PlayIcon,
  SpeakerWaveIcon,
  StopIcon,
} from '@heroicons/react/24/outline';

interface VideoPlayerProps {
  contentId: number;
  videoId: number;
  onComplete: (contentId: number) => void;
}

const speedOptions = [1, 1.25, 1.5, 1.75, 2];
const SEEK_STEP_SEC = 5;

const VideoPlayer: React.FC<VideoPlayerProps> = ({ contentId, videoId, onComplete }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isStopped, setIsStopped] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [leaseSuspended, setLeaseSuspended] = useState(false);

  const {
    streamUrl,
    format,
    error: leaseError,
    videoRef,
    reload,
    applyResumeOnMetadata,
    recoverFromStreamError,
  } = useSecurePlaybackStream('video', videoId, {
    active: !leaseSuspended,
  });

  useEffect(() => {
    setIsStopped(false);
    setLeaseSuspended(false);
    setPlaybackRate(1);
  }, [videoId]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const anyDoc = document as Document & { webkitFullscreenElement?: Element | null };
      setIsFullscreen(Boolean(document.fullscreenElement || anyDoc.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange as EventListener);
    };
  }, []);

  const seekBy = (deltaSec: number) => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const next = Math.min(Math.max(video.currentTime + deltaSec, 0), video.duration);
    try {
      video.currentTime = next;
    } catch {
      /* ignore seek until media is ready */
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('select, input, textarea, [contenteditable="true"]')) return;
      const active = document.activeElement;
      const playerFocused = container === active || container.contains(active);
      const playerHovered = container.matches(':hover');
      if (!playerFocused && !playerHovered) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        seekBy(-SEEK_STEP_SEC);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        seekBy(SEEK_STEP_SEC);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEnded = async () => {
    try {
      onComplete(contentId);
    } catch (error) {
      console.error(error);
    }
  };

  // Suspending deactivates the lease hook, which releases the lease for this video and
  // stops the heartbeat — no manual release, so no orphan heartbeat hits a missing lease.
  const handleStop = () => {
    videoRef.current?.pause();
    setIsStopped(true);
    setLeaseSuspended(true);
  };

  const handleResume = () => {
    setIsStopped(false);
    setLeaseSuspended(false);
    void reload();
  };

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video || !isStopped) return;
    video.pause();
    video.currentTime = 0;
  };

  const getFullscreenElement = () =>
    document.fullscreenElement ||
    (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ||
    null;

  const requestElementFullscreen = async (element: HTMLElement) => {
    const anyEl = element as HTMLElement & {
      requestFullscreen?: () => Promise<void>;
      webkitRequestFullscreen?: () => void;
      msRequestFullscreen?: () => void;
    };
    if (anyEl.requestFullscreen) {
      await anyEl.requestFullscreen();
      return;
    }
    if (anyEl.webkitRequestFullscreen) {
      anyEl.webkitRequestFullscreen();
      return;
    }
    if (anyEl.msRequestFullscreen) {
      anyEl.msRequestFullscreen();
    }
  };

  const exitElementFullscreen = async () => {
    const anyDoc = document as Document & {
      exitFullscreen?: () => Promise<void>;
      webkitExitFullscreen?: () => void;
      msExitFullscreen?: () => void;
    };
    if (anyDoc.exitFullscreen) {
      await anyDoc.exitFullscreen();
      return;
    }
    if (anyDoc.webkitExitFullscreen) {
      anyDoc.webkitExitFullscreen();
      return;
    }
    if (anyDoc.msExitFullscreen) {
      anyDoc.msExitFullscreen();
    }
  };

  const toggleFullscreen = async () => {
    try {
      if (getFullscreenElement()) {
        await exitElementFullscreen();
        return;
      }

      const container = containerRef.current;
      const video = videoRef.current;
      if (container) {
        await requestElementFullscreen(container);
        return;
      }

      // iOS Safari: only video element supports fullscreen
      const iosVideo = video as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
      iosVideo?.webkitEnterFullscreen?.();
    } catch (error) {
      console.error('Fullscreen failed', error);
      const iosVideo = videoRef.current as (HTMLVideoElement & { webkitEnterFullscreen?: () => void }) | null;
      iosVideo?.webkitEnterFullscreen?.();
    }
  };

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      className={`relative z-10 overflow-hidden border border-white/10 bg-black shadow-2xl outline-none ${
        isFullscreen ? 'h-screen w-screen rounded-none' : 'h-full rounded-none lg:rounded-2xl'
      }`}
    >
      {streamUrl ? (
        <>
          <SecureVideo
            format={format}
            src={streamUrl}
            videoRef={videoRef}
            controls
            autoPlay
            preload="metadata"
            controlsList="nodownload noplaybackrate noremoteplayback"
            disablePictureInPicture
            disableRemotePlayback
            onContextMenu={(event) => event.preventDefault()}
            onDoubleClick={(event) => {
              event.preventDefault();
              void toggleFullscreen();
            }}
            onEnded={handleEnded}
            onPlay={handlePlay}
            onError={() => recoverFromStreamError()}
            onLoadedMetadata={(event) => {
              applyResumeOnMetadata(event.currentTarget, playbackRate);
            }}
            className="h-full w-full bg-black object-contain"
          />
          <VideoWatermark />
        </>
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center text-sm text-white/70">
          {leaseError ? (
            <span
              className={
                leaseError.includes('khóa') || leaseError.includes('thiết bị')
                  ? 'text-red-300'
                  : 'text-white/70'
              }
            >
              {leaseError}
            </span>
          ) : (
            <span>Đang tải video...</span>
          )}
        </div>
      )}

      {/* Top-right: tránh vùng native controls ở đáy video (hit-test bị browser chiếm) */}
      <div className="pointer-events-none absolute right-2 top-2 z-30 flex items-center gap-1.5 text-white lg:right-3 lg:top-3 lg:gap-2">
        <div className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-1.5 text-[11px] backdrop-blur lg:gap-2 lg:px-3 lg:py-2 lg:text-xs">
          <SpeakerWaveIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
          <span className="hidden sm:inline">Tốc độ</span>
          <select
            value={playbackRate}
            onChange={(event) => {
              const nextRate = Number(event.target.value);
              setPlaybackRate(nextRate);
              if (videoRef.current) {
                videoRef.current.playbackRate = nextRate;
              }
            }}
            className="rounded border border-white/20 bg-black/80 px-1 py-0.5 text-white outline-none"
          >
            {speedOptions.map((speed) => (
              <option key={speed} value={speed} style={{ color: '#111827', backgroundColor: '#ffffff' }}>
                {speed}x
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => void toggleFullscreen()}
          className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 backdrop-blur hover:bg-black/80 lg:h-10 lg:w-10"
          aria-label={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
          title={isFullscreen ? 'Thoát toàn màn hình' : 'Toàn màn hình'}
        >
          {isFullscreen ? <ArrowsPointingInIcon className="h-5 w-5" /> : <ArrowsPointingOutIcon className="h-5 w-5" />}
        </button>
      </div>

      <div className="pointer-events-none absolute bottom-16 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 text-white lg:bottom-20">
        <button
          type="button"
          onClick={() => seekBy(-SEEK_STEP_SEC)}
          className="pointer-events-auto inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-black/60 px-2 text-xs font-semibold backdrop-blur hover:bg-black/80 lg:h-10 lg:min-w-10 lg:text-sm"
          aria-label={`Tua lùi ${SEEK_STEP_SEC} giây`}
          title={`Tua lùi ${SEEK_STEP_SEC}s (←)`}
        >
          −{SEEK_STEP_SEC}s
        </button>
        <button
          type="button"
          onClick={handleStop}
          className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 backdrop-blur hover:bg-black/80 lg:h-10 lg:w-10"
          aria-label="Dừng phát"
        >
          <StopIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={handleResume}
          className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 backdrop-blur hover:bg-black/80 lg:h-10 lg:w-10"
          aria-label="Phát lại"
        >
          <PlayIcon className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => seekBy(SEEK_STEP_SEC)}
          className="pointer-events-auto inline-flex h-9 min-w-9 items-center justify-center rounded-full bg-black/60 px-2 text-xs font-semibold backdrop-blur hover:bg-black/80 lg:h-10 lg:min-w-10 lg:text-sm"
          aria-label={`Tua tới ${SEEK_STEP_SEC} giây`}
          title={`Tua tới ${SEEK_STEP_SEC}s (→)`}
        >
          +{SEEK_STEP_SEC}s
        </button>
      </div>
    </div>
  );
};

export default VideoPlayer;
